import React from "react";
import { createPortal } from "react-dom";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { buildFillRulesPayload, fillRulesEqual, hasFillRuleErrors } from "../lib/productCompartmentFillRules.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import ProductCompartmentFillRulesControl from "./ProductCompartmentFillRulesControl.jsx";

const BUTTON = "inline-flex h-8 items-center justify-center rounded-[4px] border px-3 text-xs font-semibold focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";

export default function DispatchCompartmentFillDialog({ policy, disabled = false, onClose }) {
  const dialogRef = React.useRef(null);
  const titleId = React.useId();
  const [loaded, setLoaded] = React.useState(null);
  const [draft, setDraft] = React.useState([]);
  const [loadError, setLoadError] = React.useState("");
  const [reload, setReload] = React.useState(0);
  const { dispatchDate, dispatchPeriod, isSaving } = policy;

  React.useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.showModal();
    return () => {
      document.body.style.overflow = previousOverflow;
      // The menu closes when opening this dialog; restore its visible trigger.
      if (previousFocus?.isConnected && !previousFocus.closest?.("[hidden]")) previousFocus.focus();
    };
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    setLoaded(null);
    setLoadError("");
    policy.clearError?.();
    const params = new URLSearchParams({ dispatch_period: dispatchPeriod });
    fetchWithSupabaseAuth(`/api/planning/dispatch/${encodeURIComponent(dispatchDate)}/product-compartment-fill?${params}`, { signal: controller.signal })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok || !Array.isArray(payload.product_compartment_fill_rules) || !payload.runtime_revision) {
          throw new Error(typeof payload.detail === "string" ? payload.detail : "Impossible de charger les limites de chargement.");
        }
        if (!controller.signal.aborted) {
          setLoaded(payload);
          setDraft(payload.product_compartment_fill_rules);
        }
      })
      .catch((error) => { if (!controller.signal.aborted) setLoadError(error.message); });
    return () => controller.abort();
  }, [dispatchDate, dispatchPeriod, reload]);

  const hasChanges = loaded && !fillRulesEqual(draft, loaded.product_compartment_fill_rules);
  const canSave = hasChanges && !hasFillRuleErrors(draft) && !disabled && !isSaving;
  async function save() {
    if (!canSave) return;
    const result = await policy.save(undefined, { rules: buildFillRulesPayload(draft), expected_runtime_revision: loaded.runtime_revision });
    if (result) onClose();
  }
  const dateLabel = new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "long", year: "numeric" }).format(new Date(`${dispatchDate}T12:00:00`));
  const root = getReactPortalRoot();
  if (!root) return null;
  return createPortal(
    <dialog ref={dialogRef} aria-labelledby={titleId}
      className="fixed inset-0 m-auto max-h-[calc(100dvh-32px)] w-[min(760px,calc(100vw-24px))] max-w-none overflow-y-auto rounded-[4px] border border-slate-300 bg-white p-4 text-slate-900 shadow-xl backdrop:bg-slate-900/35"
      onCancel={(event) => { event.preventDefault(); if (!isSaving) onClose(); }}>
      <h2 id={titleId} className="m-0 text-base font-semibold">Expansion des produits — {dateLabel}</h2>
      <p className="mb-3 mt-1 text-xs text-slate-600">Pour cette date, Jour et Soir. Limite le remplissage de chaque compartiment pour les produits choisis, camions sans compartiments compris.</p>
      {loaded?.product_compartment_fill_enabled === false ? <p className="mb-2 rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1 text-xs">Les limites sont désactivées. Vous pouvez préparer les règles puis les activer dans « Plus ».</p> : null}
      {loaded ? <ProductCompartmentFillRulesControl canEditSetting={!disabled && !isSaving}
        setting={{ key: "rules", product_code_options: loaded.product_code_options }} value={draft} onChange={(_key, rules) => setDraft(rules)} />
        : !loadError ? <p role="status" className="text-sm">Chargement…</p> : null}
      {loaded ? <p className="my-2 text-xs text-slate-500">Arrondi vers le bas à la demi-tonne. Si plusieurs règles correspondent, le plus petit pourcentage s’applique. 100 % = aucune limite. Les tournées seront recalculées à votre demande.</p> : null}
      {loadError || policy.error ? <div role="alert" className="my-2 text-xs text-red-700">
        <p>{loadError || policy.error}</p>
        <button type="button" className={`${BUTTON} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`} disabled={isSaving}
          onClick={() => setReload((value) => value + 1)}>Recharger les règles</button>
      </div> : null}
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className={`${BUTTON} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50`} disabled={isSaving} onClick={onClose}>Annuler</button>
        <button type="button" className={`${BUTTON} border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong`} disabled={!canSave} onClick={save}>{isSaving ? "Enregistrement…" : "Enregistrer"}</button>
      </div>
    </dialog>, root,
  );
}
